import {
  buildRecommendationOptions,
  CreateItemRecommendation,
  matchRecommendations,
} from "./createItemRecommendations.js";
import { InputOption } from "./createItemInputOptions.js";

export type CreateItemFilterOptionsLabels = {
  recommendationsHeader: string;
  addingTemplate: string;
  templatesHeader: string;
  productsHeader: string;
  productListsHeader: string;
  nothingFound: string;
};

/**
 * Composes the create-item combobox dropdown for the current input state.
 *
 * With an empty input only the entry points are offered (search modals,
 * paste, recommended templates). While the user is typing, the sub-item type
 * options come first, followed by the entry points, the fuzzy-matched
 * recommendations, and the async template/product search sections with their
 * loading/"Nothing found" headers.
 */
export function buildCreateItemFilterOptions({
  options,
  inputValue,
  inputValueDebounced,
  creatingItem,
  canPasteItem,
  showFisSolr,
  allowTemplatesSearch,
  allowTemplatesInDropdown,
  allowProductsSearch,
  allowProductsInDropdown,
  allowProductListsSearch,
  allowProductListsInDropdown,
  allowRecommendations,
  subItemTypesOptions,
  searchProductsOptions,
  searchProductsFisSolrOptions,
  searchTemplatesOptions,
  searchProductListsOptions,
  pasteOptions,
  recommendations,
  recommendationsLoading,
  searchingTemplates,
  searchingProducts,
  searchingProductLists,
  labels,
}: {
  /** The options currently held by the Autocomplete (template/product search results). */
  options: InputOption[];
  /** The live input value the Autocomplete filters for. */
  inputValue: string;
  inputValueDebounced: string;
  creatingItem: boolean;
  canPasteItem: boolean;
  showFisSolr: boolean;
  allowTemplatesSearch: boolean;
  allowTemplatesInDropdown: boolean;
  allowProductsSearch: boolean;
  allowProductsInDropdown: boolean;
  allowProductListsSearch: boolean;
  allowProductListsInDropdown: boolean;
  allowRecommendations: boolean;
  subItemTypesOptions: InputOption[];
  searchProductsOptions: InputOption[];
  searchProductsFisSolrOptions: InputOption[];
  searchTemplatesOptions: InputOption[];
  searchProductListsOptions: InputOption[];
  pasteOptions: InputOption[];
  recommendations: CreateItemRecommendation[];
  recommendationsLoading: boolean;
  searchingTemplates: boolean;
  searchingProducts: boolean;
  searchingProductLists: boolean;
  labels: CreateItemFilterOptionsLabels;
}): InputOption[] {
  const searchEntryPoints: InputOption[] = [
    ...(allowProductsSearch
      ? showFisSolr
        ? searchProductsFisSolrOptions
        : searchProductsOptions
      : []),
    ...(allowProductListsSearch ? searchProductListsOptions : []),
    ...(allowTemplatesSearch ? searchTemplatesOptions : []),
  ];

  if (inputValue === "") {
    return [
      ...searchEntryPoints,
      ...(canPasteItem ? pasteOptions : []),
      ...buildRecommendationOptions({
        items: recommendations,
        allowRecommendations,
        recommendationsLoading,
        headerTitle: labels.recommendationsHeader,
        rowInputValue: labels.addingTemplate,
      }),
    ];
  }

  const autoCompleteTemplateOptions = options.filter(o => o.isTemplate);
  const autoCompleteProductOptions = options.filter(o => o.isProduct);
  const autoCompleteProductListOptions = options.filter(o => o.isProductList);

  const areAnyTemplatesLoading =
    searchingTemplates || inputValueDebounced !== inputValue;

  const areProductsLoading =
    searchingProducts || inputValueDebounced !== inputValue;

  const areProductListsLoading =
    searchingProductLists || inputValueDebounced !== inputValue;

  // Fuzzy-match the typed term against recommendation teaser titles so
  // matching recommended templates appear in their own section while
  // the user is searching, with the same create-from-recommendation
  // action as the empty-search list.
  const matchedRecommendations = allowRecommendations
    ? matchRecommendations(inputValue, recommendations)
    : [];

  return [
    ...subItemTypesOptions,
    ...searchEntryPoints,
    ...(!creatingItem
      ? buildRecommendationOptions({
          items: matchedRecommendations,
          allowRecommendations,
          recommendationsLoading,
          headerTitle: labels.recommendationsHeader,
          rowInputValue: labels.addingTemplate,
        })
      : []),
    ...(allowTemplatesInDropdown && !creatingItem
      ? [
          {
            id: "templates-header",
            isHeader: true as const,
            title: labels.templatesHeader,
            isLoading: areAnyTemplatesLoading,
            hint:
              !areAnyTemplatesLoading &&
              autoCompleteTemplateOptions.length === 0
                ? labels.nothingFound
                : undefined,
          },
          ...(!areAnyTemplatesLoading && autoCompleteTemplateOptions.length > 0
            ? autoCompleteTemplateOptions
            : []),
        ]
      : []),
    ...(allowProductsInDropdown && !creatingItem
      ? [
          {
            id: "products-header",
            isHeader: true as const,
            title: labels.productsHeader,
            isLoading: areProductsLoading,
            hint:
              !areProductsLoading && autoCompleteProductOptions.length === 0
                ? labels.nothingFound
                : undefined,
          },
          ...(!areProductsLoading && autoCompleteProductOptions.length > 0
            ? autoCompleteProductOptions
            : []),
        ]
      : []),
    ...(allowProductListsInDropdown && !creatingItem
      ? [
          {
            id: "product-lists-header",
            isHeader: true as const,
            title: labels.productListsHeader,
            isLoading: areProductListsLoading,
            hint:
              !areProductListsLoading &&
              autoCompleteProductListOptions.length === 0
                ? labels.nothingFound
                : undefined,
          },
          ...(!areProductListsLoading &&
          autoCompleteProductListOptions.length > 0
            ? autoCompleteProductListOptions
            : []),
        ]
      : []),
  ];
}
